import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { ErrorBoundary } from 'react-error-boundary';
import {
  ConnectComponentsProvider,
  ConnectPayouts,
  ConnectDisputesList,
} from '@stripe/react-connect-js';
import { TWButton } from '../../../../components/ui';
import connectApi from '../../../../services/connectApi';
import useAccountSession from '../../../connect/use-account-session';
import useConnectInstance from '../../../connect/use-connect-instance';
import { describeSchedule } from '../../../connect/finance/connect-schedule';
import { formatUtcDay } from '../../../connect/finance/connect-finance-tables';
import { formatCurrency } from '../../../../utils/baseUtils';
import { apiMessage } from '../../../connect/connect-api-message';

// Keys useConnectInstance's own effect — see that hook's own comment: never
// sent to Stripe or to the server, only used so this mount gets an instance
// separate from any other surface's (settlement's admin finance session,
// settings' own session, etc). Fix wave B, cross-repo: `account_management`
// is gone from the `admin` Account Session surface — setting every write
// flag false on it did not make that component read-only, it made it the
// wrong component to hand an admin at all: it is the editable
// account-details component, and an admin must never be able to edit a
// restaurant's account details. The admin surface carries `payouts`,
// `disputes` and — since 2026-10-20, for the Account Activity tab's
// read-only balances card (company-payouts-tab.js) — `balances`, every
// feature false; this block mounts the first two. The bank on file below
// comes from our own read (GET Connect/Balance, via the same `balance`
// state already fetched for the payout schedule) instead of a Stripe mount.
const SUPPORT_COMPONENTS = ['payouts', 'disputes'];

// A support agent taking a call about this restaurant's money has no Stripe
// dashboard to open: the account was created with Stripe's own dashboard
// switched off, and Thisway cannot open Stripe on the restaurant's behalf
// either. This mounts the very same read components the restaurant itself
// sees, through the `admin` account-session surface — every write capability
// that surface grants is already false on the server, so this component adds
// no capability of its own; it only asks for the surface. Collapsed like
// ConnectSettlement beside it, for the same reason: nothing in the collapsed
// state may name a fee, a payout, a balance or a margin (connect-tab.test.js
// pins this for the whole tab), and there is nothing to read for a company
// with no account yet either.
//
// `isReady` is a second, separate gate from `hasAccount` (fix wave B): three
// surfaces mean three different things by "ready" — the finance page
// requires an account and a ready status, settings requires a ready status,
// and this block used to require only that an account exists. That let it
// mount Stripe's components against an account Stripe has not finished
// checking — the empty-frame case the finance gate exists to avoid — under
// copy that promises "what this restaurant sees". Gated here the same way
// the finance page gates it.
const ConnectSupport = ({ companyId, hasAccount, isReady }) => {
  const [open, setOpen] = useState(false);
  const isDark = useSelector((state) => state.commonReducer.isDark);
  const { fetchSecret, error } = useAccountSession(companyId, 'admin');
  // No Account Session is minted, and useConnectInstance does nothing, until
  // an admin actually opens this — the same reason the section starts
  // collapsed at all. Closing it again drops the instance (useConnectInstance
  // treats a null secretSource as "nothing to do yet"); reopening mints a
  // fresh one rather than reusing a possibly-expired one.
  const { instance, error: instanceError } = useConnectInstance(
    open ? fetchSecret : null,
    SUPPORT_COMPONENTS,
    isDark,
  );

  const [balance, setBalance] = useState(null);
  const [balanceError, setBalanceError] = useState('');

  // The balance read is our own record, entirely independent of the Account
  // Session above — it needs no Stripe embed and no `instance` to succeed, so
  // it is not gated on either (only on the same readiness gate the rest of
  // the opened block is gated on).
  useEffect(() => {
    if (!open || !hasAccount || !isReady || !companyId) return undefined;
    let active = true;
    setBalanceError('');
    (async () => {
      try {
        const next = connectApi.toConnectBalance(await connectApi.getConnectBalance(companyId));
        if (!active) return;
        if (!next) throw new Error('empty');
        setBalance(next);
      } catch (caught) {
        if (!active) return;
        setBalance(null);
        setBalanceError(apiMessage(caught) || "Couldn't read the payout schedule.");
      }
    })();
    return () => {
      active = false;
    };
  }, [open, hasAccount, isReady, companyId]);

  const sessionFailure = error || instanceError?.message || '';
  const hasPayout = !!balance?.nextPayoutDate;
  // A payout with a date but no amount must never print a real date beside
  // an invented "$0.00" — render the date alone instead.
  const nextPayoutLine = hasPayout
    ? balance.nextPayoutAmount != null
      ? `${formatUtcDay(balance.nextPayoutDate)} — ${formatCurrency(balance.nextPayoutAmount)}`
      : formatUtcDay(balance.nextPayoutDate)
    : describeSchedule(balance);
  // The three bank fields are documented as moving together (all null, or
  // all populated), but this line must not depend on that holding: a payload
  // carrying a name with no last four must render the name alone, never
  // "Chase ····null".
  const bankLine = balance?.bankName
    ? balance.bankLast4
      ? `${balance.bankName} ····${balance.bankLast4}`
      : balance.bankName
    : 'No bank on file yet.';

  return (
    <section className="py-6">
      <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Support</h3>
      {!hasAccount ? (
        // Deliberately NOT ConnectSettlement's own "No Connect account yet."
        // sentence — the two blocks sit on the same collapsed screen, and a
        // second identical sentence would make that panel's own `getByText`
        // ambiguous.
        <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">
          There's no Stripe account for this company yet.
        </p>
      ) : !isReady ? (
        <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">
          Stripe hasn't finished checking this account yet — there's nothing
          to show here until it has.
        </p>
      ) : (
        <>
          <p className="mt-3 max-w-2xl text-sm text-slate-500 dark:text-slate-400">
            What this restaurant sees in Stripe — bank on file, transfers and
            disputes — read-only. Every write capability here is switched off
            on the server, so there's no button to look for.
          </p>
          <div className="mt-4">
            <TWButton
              size="sm"
              label={open ? 'Hide transfers and disputes' : 'Show transfers and disputes'}
              onClick={() => setOpen((prev) => !prev)}
            />
          </div>
          {open ? (
            <div className="mt-4 space-y-5">
              <div>
                <p className="text-[0.65rem] font-semibold uppercase tracking-[0.15em] text-slate-500 dark:text-slate-400">
                  Bank
                </p>
                <p className="mt-1 text-sm text-slate-900 dark:text-slate-100">
                  {balanceError ? (
                    <span className="text-rose-600 dark:text-rose-400">{balanceError}</span>
                  ) : balance ? (
                    bankLine
                  ) : (
                    'Loading…'
                  )}
                </p>
              </div>

              <div>
                <p className="text-[0.65rem] font-semibold uppercase tracking-[0.15em] text-slate-500 dark:text-slate-400">
                  Next payout
                </p>
                <p className="mt-1 text-sm text-slate-900 dark:text-slate-100">
                  {balanceError ? (
                    <span className="text-rose-600 dark:text-rose-400">{balanceError}</span>
                  ) : balance ? (
                    nextPayoutLine
                  ) : (
                    'Loading…'
                  )}
                </p>
              </div>

              {sessionFailure ? (
                // Checked before `instance`: useConnectInstance hands back
                // a truthy `instance` from loadConnectAndInitialize's own
                // synchronous return even when the secret it was given
                // never resolves — the real fetch happens in the
                // background. A stale `error` from useAccountSession is the
                // one signal that actually says the mint failed, and it
                // must win over a technically-truthy instance, or this
                // failure never has anywhere to show.
                <p className="text-sm text-rose-600 dark:text-rose-400">{sessionFailure}</p>
              ) : instance ? (
                // Narrow, on top of the boundary connect-tab.js already
                // hoists around this whole component (see
                // connect-support-boundary.js's own comment for why that one
                // has to sit out there instead of in here). That outer
                // boundary catches a throw from the hooks above, which this
                // component could never catch for itself; this one keeps a
                // throw from a mounted Stripe component — the payouts widget,
                // say — from also blanking the heading, the toggle and the
                // bank/next-payout figures above, which come from our own
                // read and have nothing to do with the Stripe session.
                <ErrorBoundary
                  fallback={
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      This section could not load. Refresh the page to try again.
                    </p>
                  }
                >
                  <ConnectComponentsProvider connectInstance={instance}>
                    <div>
                      <p className="text-[0.65rem] font-semibold uppercase tracking-[0.15em] text-slate-500 dark:text-slate-400">
                        Payouts
                      </p>
                      <div className="mt-2">
                        <ConnectPayouts />
                      </div>
                    </div>
                    <div className="mt-5">
                      <p className="text-[0.65rem] font-semibold uppercase tracking-[0.15em] text-slate-500 dark:text-slate-400">
                        Disputes
                      </p>
                      <div className="mt-2">
                        <ConnectDisputesList />
                      </div>
                    </div>
                  </ConnectComponentsProvider>
                </ErrorBoundary>
              ) : null}
            </div>
          ) : null}
        </>
      )}
    </section>
  );
};

export default ConnectSupport;
